import React, { useContext, useEffect, useState } from 'react';
import Check from 'mdi-material-ui/Check';
import classNames from 'clsx';
import { useSelector } from 'react-redux';

import { ReactComponent as DocIcon } from 'app/assets/icons/doc.svg';
import { NewButton } from 'app/components/NewButton';
import { InstallmentsEnum, LabelEnum, MessageTypeEnum } from 'app/shared/enums';
import { useNotification } from 'app/shared/hooks/useNotification';
import { MubiInstallmentsContext } from 'app/components/MubiInstallmentsRadioOption/useMubiInstallments.context';
import { DocumentService } from 'app/services/document.service';
import { InstallmentOffer } from 'app/components/MubiInstallmentsRadioOption/MubiInstallmentsRadioOption.interfaces';
import { HiProPaymentService } from 'app/services/hiProPayment.services';
import {
  ErrorConfig,
  ErrorDecision,
  getMubiInstalmentError,
} from 'app/shared/helpers/getMubiInstalmentError';
import { getDownloadPdfFile } from 'app/shared/helpers/getDownloadPdfFile';
import { selectIsFormMicroCenterActive } from 'app/store/selectors/selectIsFormMicroCenterActive';

import { useStyles } from './CreditGranted.styles';
import { CreditGrantedProps } from './CreditGranted.interface';

export default function CreditGranted(props: CreditGrantedProps) {
  const classes = useStyles();
  const notify = useNotification();
  const isFormMicroCenterActive = useSelector(selectIsFormMicroCenterActive);
  const {
    installmentOffer,
    hiProToken,
    closeModal,
    setErrorOnModal,
    mubiInstallmentsDataLayerEvent,
    setActiveStep,
  } = useContext(MubiInstallmentsContext);

  const [instalmentOfferForUser, setInstalmentOfferForUser] = useState<
    InstallmentOffer | undefined
  >(installmentOffer);

  const goToNextStep = () => {
    mubiInstallmentsDataLayerEvent(
      InstallmentsEnum.EventNameLoanTermsNextStep,
      InstallmentsEnum.EventStepNameLoanTerms
    );

    setActiveStep(5);
  };

  const getInstalmentOffer = async () => {
    if (!hiProToken) {
      return;
    }
    await HiProPaymentService.getHiProCreditDecision({
      token: hiProToken,
    })
      .then(({ data }) => {
        setInstalmentOfferForUser(data);
      })
      .catch(({ response }) => {
        const hiProError =
          response?.data &&
          getMubiInstalmentError(
            response.data.errorKey || response.data.message
          );

        if (!hiProError) {
          notify(MessageTypeEnum.Danger, LabelEnum.ServerError);
        } else if (hiProError === ErrorDecision.NegativeDecision) {
          setActiveStep(7);
        } else {
          closeModal();
          setErrorOnModal(hiProError as ErrorConfig);
        }
      });
  };
  useEffect(() => {
    if (!instalmentOfferForUser) {
      getInstalmentOffer();
    }
    mubiInstallmentsDataLayerEvent(
      InstallmentsEnum.EventModalStep,
      InstallmentsEnum.EventStepNameLoanTerms
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!instalmentOfferForUser) {
    return null;
  }

  const downloadDocument = async (link: string, name: string) => {
    try {
      await getDownloadPdfFile(link, name);
    } catch (error) {
      notify(MessageTypeEnum.Danger, LabelEnum.ServerError);
    }
  };

  const loanCost = (
    Number(instalmentOfferForUser.grossLoanAmount) -
    Number(instalmentOfferForUser.nettLoanAmount)
  ).toFixed(2);

  return (
    <div className={classes.wrapper}>
      <div className={classes.successWrapper}>
        <Check className={classes.successIcon} />
        <div className={classes.successTitle}>
          {InstallmentsEnum.CreditGranted}
        </div>
      </div>
      <div className={classes.creditInformationWrapper}>
        <div className={classes.creditInformation}>
          <div>
            {LabelEnum.NumberOfInstallments}:{' '}
            {instalmentOfferForUser.installmentCount}
          </div>
          <div>
            {InstallmentsEnum.AmountOfInstallment}:{' '}
            {instalmentOfferForUser.installmentAmount}&nbsp;{LabelEnum.ZL}
          </div>
          <div>
            {InstallmentsEnum.FirstInstallmentPaymentBy}:{' '}
            {instalmentOfferForUser.firstInstallmentDate
              .split('-')
              .reverse()
              .join('.')}
          </div>
        </div>
        <div className={classes.creditDetails}>
          <div>
            <span>
              {InstallmentsEnum.AllAmount}:{' '}
              {instalmentOfferForUser.grossLoanAmount}&nbsp;{LabelEnum.ZL}
            </span>
            <span>
              {LabelEnum.RRSO}: {instalmentOfferForUser.rrso}%
            </span>
          </div>
          <div>
            <span>
              {InstallmentsEnum.Cost}: {loanCost}
              &nbsp;{LabelEnum.ZL}
            </span>
            <span>
              {InstallmentsEnum.InterestRate}:{' '}
              {Number(instalmentOfferForUser.commissionPercent) * 100}%
            </span>
          </div>
        </div>
        <div className={classes.documents}>
          {instalmentOfferForUser.documents.map(({ code, name }) => {
            const link = DocumentService.getHiProDocumentDownloadLink(
              hiProToken as string,
              code
            );
            return (
              <span
                tabIndex={0}
                onClick={() => downloadDocument(link, name)}
                onKeyDown={(e) => {
                  if (
                    e.target === e.currentTarget &&
                    (e.key === 'Enter' || e.key === ' ')
                  ) {
                    e.preventDefault();
                    downloadDocument(link, name);
                  }
                }}
                className={classNames(
                  classes.documentLink,
                  'focusVisibleOutline'
                )}
                key={code}
              >
                <DocIcon />
                <span>{name}</span>
              </span>
            );
          })}
        </div>
      </div>
      <NewButton
        onClick={goToNextStep}
        fullWidth
        title={InstallmentsEnum.AcceptInstalment}
        variant="contained"
        color="primary"
        className={classes.button}
        data-cy="accept-instalment"
        rounded={isFormMicroCenterActive}
      />
    </div>
  );
}
